import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Users, Books, FilePdf, PencilLine, GraduationCap, Newspaper, CheckCircle, XCircle, TrendUp, CircleNotch } from "@phosphor-icons/react";

const Card = ({ icon: Icon, label, value, sub, accent = "#1E3A8A" }) => (
    <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-5 flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</span>
            <Icon size={18} weight="duotone" style={{ color: accent }} />
        </div>
        <div className="font-heading font-black text-3xl text-slate-900 tabular-nums">{value}</div>
        {sub && <div className="text-[11px] text-slate-500">{sub}</div>}
    </div>
);

const StatusPill = ({ ok, label }) => (
    <div className={`flex items-center gap-1.5 text-xs font-semibold ${ok ? "text-emerald-700" : "text-amber-700"}`}>
        {ok ? <CheckCircle size={16} weight="fill" /> : <XCircle size={16} weight="fill" />}
        <span>{label}</span>
    </div>
);

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        (async () => {
            try { const { data } = await api.get("/admin/stats"); setData(data); }
            catch (e) { setError(e.response?.data?.detail || "Failed to load stats"); }
        })();
    }, []);

    if (error) return <div className="text-red-700 text-sm">{error}</div>;
    if (!data) return <div className="text-slate-500 flex items-center gap-2 text-sm"><CircleNotch size={16} className="animate-spin" /> Loading stats…</div>;

    return (
        <div className="space-y-8">
            <section>
                <h2 className="font-heading font-bold text-sm uppercase tracking-widest text-slate-500 mb-3">Users</h2>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                    <Card icon={Users} label="Total Users" value={data.users.total} sub={`${data.users.admins} admin(s)`} />
                    <Card icon={CheckCircle} label="Active Sessions" value={data.users.active_sessions} accent="#0F766E" />
                </div>
            </section>

            <section>
                <h2 className="font-heading font-bold text-sm uppercase tracking-widest text-slate-500 mb-3">Activity</h2>
                <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                    <Card icon={TrendUp} label="MCQ Sessions" value={data.activity.mcq_sessions_total} sub={`${data.activity.mcq_sessions_submitted} submitted`} accent="#B45309" />
                    <Card icon={PencilLine} label="Mains Submissions" value={data.activity.mains_submissions} accent="#7C3AED" />
                </div>
            </section>

            <section>
                <h2 className="font-heading font-bold text-sm uppercase tracking-widest text-slate-500 mb-3">Content</h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card icon={Books} label="Questions" value={data.content.questions} sub={`${data.content.questions_from_pyq} from PYQ`} />
                    <Card icon={FilePdf} label="PYQ Papers" value={data.content.pyq_papers} />
                    <Card icon={PencilLine} label="Mains Prompts" value={data.content.mains_prompts} />
                    <Card icon={GraduationCap} label="Study Material" value={data.content.study_materials} />
                    <Card icon={Newspaper} label="CA date" value={data.daily_ca_today.date} sub="Today (IST)" accent="#0F766E" />
                </div>
            </section>

            <section className="grid md:grid-cols-2 gap-4">
                <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-5">
                    <h3 className="font-heading font-bold text-lg mb-3">Today&apos;s Current Affairs</h3>
                    <div className="space-y-2">
                        <StatusPill ok={data.daily_ca_today.mcqs} label={`Daily MCQs ${data.daily_ca_today.mcqs ? "ready" : "not generated"}`} />
                        <StatusPill ok={data.daily_ca_today.editorials} label={`Editorials ${data.daily_ca_today.editorials ? "ready" : "not generated"}`} />
                        <StatusPill ok={data.daily_ca_today.articles} label={`Articles ${data.daily_ca_today.articles ? "ready" : "not generated"}`} />
                    </div>
                    <a href="/admin/current-affairs" className="inline-block mt-4 text-xs font-semibold text-[#1E3A8A] hover:underline">Manage Current Affairs →</a>
                </div>
                <div className="bg-white border-2 border-[#BFD3FF] rounded-2xl p-5">
                    <h3 className="font-heading font-bold text-lg mb-3">Top attempted subjects</h3>
                    {data.activity.top_subjects.length === 0 ? (
                        <div className="text-sm text-slate-500">No attempts yet.</div>
                    ) : (
                        <ul className="space-y-1.5">
                            {data.activity.top_subjects.map((s, i) => (
                                <li key={i} className="flex items-center justify-between text-sm border-b border-slate-100 last:border-0 pb-1.5">
                                    <span className="font-medium text-slate-700">{s.subject}</span>
                                    <span className="text-[11px] font-bold text-[#1E3A8A] tabular-nums">{s.attempts}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </section>
        </div>
    );
}
